import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import usePageMeta from "@/hooks/usePageMeta";

const principles = [
  { icon: "ri-shield-check-line", title: "内容真实", desc: "只推荐真实存在的短番，不虚构、不夸大，所有评价基于实际观看体验。" },
  { icon: "ri-eye-line", title: "亲测收录", desc: "每一部入选的泡面番都经过团队逐集试看，短篇速刷不迷路，杜绝踩坑。" },
  { icon: "ri-sparkling-line", title: "客观中立", desc: "不受商业因素影响，坚持从内容质量与观众体验出发给出推荐。" },
  { icon: "ri-compass-line", title: "口味细分", desc: "按下饭、摸鱼、搞笑、治愈等场景精细分类，让每个人都能快速找到合口味的番。" },
];

const testimonials = [
  { name: "阿橙", role: "上班族", text: "午休摸鱼就靠小水看番了，每天中午挑一集下饭番，笑一笑下午都有精神。" },
  { name: "小岛", role: "大学生", text: "一口气看完的分类超方便，周末补一个系列刚好，终于不用追番追到心累。" },
  { name: "盐盐", role: "自由职业", text: "深夜失眠的时候看治愈系短篇特别舒服，画面和名字一样温柔。" },
  { name: "木木", role: "通勤党", text: "地铁上三分钟一集刚刚好，每部都短篇速刷不迷路，已经离不开它了。" },
];

export default function About() {
  usePageMeta(
    "关于我们 · 小水看番泡面番推荐/三分钟短番内容站",
    "了解小水看番：一个专注泡面番与三分钟短番推荐的内容站，坚持亲测收录、客观中立，按口味分好类，帮你一口气看完喜欢的下饭番与摸鱼短番。",
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于小水看番",
      url: "https://afqux.cn/about",
      description: "小水看番是专注泡面番与三分钟短番推荐的内容站。",
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-secondary-50 via-background-50 to-primary-50 pt-24 pb-14">
          <div className="container-x text-center">
            <nav className="flex items-center justify-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              关于小水看番
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
              一群重度短番爱好者，用一碗泡面的时间，认真为你挑选每一部值得看的泡面番、三分钟短番与日常搞笑短篇。
            </p>
          </div>
        </section>

        <section className="container-x py-12 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                我们的品牌定位
              </h2>
              <p className="mt-4 leading-relaxed text-foreground-700">
                在这个追求『短平快』的时代，我们把散落在各个平台的优质短篇动画收集起来，用清晰的口味分类和榜单，帮你快速找到那部刚好三分钟的下饭番。
              </p>
              <p className="mt-3 leading-relaxed text-foreground-700">
                无论是忙里偷闲的摸鱼短番，还是深夜治愈的暖心短篇，我们都希望用一次点击，换取你一整天的好心情。
              </p>
              <h2 className="mt-8 font-heading text-xl font-black text-foreground-950 md:text-2xl">发展理念</h2>
              <p className="mt-4 leading-relaxed text-foreground-700">
                我们不追求数量，只追求『每一部都值得看』。用克制的收录、用心的分类和真诚的评价，陪伴每一位短篇速刷爱好者，让看番这件事变得轻松又有趣。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="container-x">
            <h2 className="text-center font-heading text-xl font-black text-foreground-950 md:text-2xl">
              看番的朋友怎么说
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-center text-sm text-foreground-600">
              这些是来自追番同好的真实反馈，感谢每一位在小水看番找到心头好的你。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <div className="flex gap-0.5 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">"{t.text}"</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-sm font-bold text-accent-700">
                      {t.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="container-x py-12 md:py-16">
          <div className="rounded-3xl bg-primary-500 px-6 py-12 text-center md:px-12">
            <h2 className="font-heading text-xl font-black text-background-50 md:text-2xl">
              也欢迎你，加入小水看番
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm text-background-50/85 md:text-base">
              有好看的泡面番或三分钟短番想推荐？有想看的分类还没上线？欢迎联系我们，一起把这份清单做得更好。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a href="mailto:hello@xiaoshuikanfan.com" className="btn-pill bg-background-50 text-primary-700 hover:bg-background-100">
                <i className="ri-mail-line"></i> hello@xiaoshuikanfan.com
              </a>
              <Link to="/biaoqian" className="btn-pill bg-foreground-950/90 text-background-50 hover:bg-foreground-950">
                去逛逛榜单 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}